// stringCrypto.js

// URL-safe encoding/decoding functions
function urlSafeEncode(data) {
    return btoa(data)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

function urlSafeDecode(encoded) {
    // Add padding back
    while (encoded.length % 4) {
        encoded += '=';
    }
    return atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
}

// Simple XOR encryption/decryption functions
function simpleEncrypt(text, key) {
    let result = '';
    for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return urlSafeEncode(result); // URL-safe Base64 encode
}

function simpleDecrypt(encryptedText, key) {
    try {
        const decoded = urlSafeDecode(encryptedText); // URL-safe Base64 decode
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
            result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
        }
        return result;
    } catch (e) {
        console.error('Decryption failed:', e);
        return encryptedText; // Return original if decryption fails
    }
}

// Web Crypto API functions
async function encryptStringWithCrypto(text, key) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    
    // Derive a proper key from the input key using PBKDF2
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(key),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
    );
    
    const cryptoKey = await window.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
    );
    
    // Generate IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt
    const encrypted = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        data
    );
    
    // Combine salt, IV and encrypted data
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);
    
    return urlSafeEncode(String.fromCharCode(...combined));
}

async function decryptStringWithCrypto(encryptedText, key) {
    try {
        const encoder = new TextEncoder();
        const decoder = new TextDecoder();
        
        // Decode URL-safe base64
        const combined = new Uint8Array(urlSafeDecode(encryptedText).split('').map(char => char.charCodeAt(0)));
        
        // Extract salt, IV and encrypted data
        const salt = combined.slice(0, 16);
        const iv = combined.slice(16, 28);
        const encrypted = combined.slice(28);
        
        // Derive the key using the same parameters
        const keyMaterial = await window.crypto.subtle.importKey(
            'raw',
            encoder.encode(key),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );
        
        const cryptoKey = await window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['decrypt']
        );
        
        // Decrypt
        const decrypted = await window.crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            cryptoKey,
            encrypted
        );
        
        return decoder.decode(decrypted);
    } catch (e) {
        console.error('Web Crypto decryption failed:', e);
        throw e;
    }
}

export async function encryptString(text, key) {
    // Try Web Crypto API first
    if (window.crypto && window.crypto.subtle) {
        try {
            return await encryptStringWithCrypto(text, key);
        } catch (e) {
            console.warn('Web Crypto failed, falling back to simple encryption');
        }
    }
    // Fallback to simple XOR
    return simpleEncrypt(text, key);
}

export async function decryptString(encryptedText, key) {
    // Try Web Crypto API first
    if (window.crypto && window.crypto.subtle) {
        try {
            return await decryptStringWithCrypto(encryptedText, key);
        } catch (e) {
            console.warn('Web Crypto failed, falling back to simple decryption');
        }
    }
    // Fallback to simple XOR
    return simpleDecrypt(encryptedText, key);
}